import {Link} from '@jobseeker/lotus/elements';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import UploadInput from '@jobseeker/lotus/elements/UploadInput';
import Loader from '@jobseeker/lotus/components/Loader';
import {Box, Skeleton, Typography} from '@mui/material';
import {enqueueSnackbar} from 'notistack';
import {
  truncateString,
  getFileBlobFromUrl,
  getFileNameFromFileUrl,
  formatBytes,
} from '@jobseeker/lotus/utils';
import {BaseSyntheticEvent, useEffect, useRef, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  ResumeAttached,
  HiristResumeAttached,
  UploadHirist,
} from '@jobseeker/lotus/icons';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type UploadFileProps = {
  title: string;
  buttonText: string;
  submitFile: boolean;
  changeFile?: boolean;
  handleSubmit: (formData: FormData) => Promise<void>;
  handleFileConfirm?: () => void;
  handleTracking?: () => void;
  formFileName?: string;
  initialFile?: string | null;
  defaultFileName?: string;
  uploadTimeStamp?: string;
  allowedFileSize?: number;
  allowedFileFormat?: string;
  alertMessage?: string;
  newUploadText?: string;
  downloadText?: string;
  nofileError?: boolean;
  profileStatus?: boolean;
  isSystemGenerated?: boolean;
};

const UploadFile = ({
  title,
  buttonText,
  submitFile,
  changeFile,
  handleSubmit,
  handleFileConfirm,
  formFileName = 'file',
  initialFile,
  defaultFileName,
  uploadTimeStamp,
  allowedFileSize = 2097152,
  allowedFileFormat = '.doc,.docx,.pdf',
  alertMessage = 'Accepted Format includes PDF, DOC & DOCX upto 2MB',
  newUploadText = 'Upload new resume',
  downloadText = 'Download',
  nofileError = false,
  profileStatus = false,
  isSystemGenerated = false,
}: UploadFileProps) => {
  const NO_FILE_NAME = 'No file chosen';
  const [fileName, setFileName] = useState(NO_FILE_NAME);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<Blob>();
  const [error, setError] = useState<string>('');
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const hiddenFileReInput = useRef<HTMLInputElement>(null);
  const [sanitisedFileName, setSanitisedFileName] = useState('');

  useEffect(() => {
    setSanitisedFileName(
      `${defaultFileName}.${fileName.split('.').pop()?.toLowerCase()}`
    );
  }, [defaultFileName, fileName]);

  const handleFileUpload = async (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const fileUploaded = event?.target?.files?.length
      ? event?.target?.files[0]
      : null;

    if (fileUploaded) {
      const validExtensions = allowedFileFormat.toLowerCase().split(',');
      const fileNameWithoutExtension =
        fileUploaded.name.substring(0, fileUploaded.name.lastIndexOf('.')) ||
        fileUploaded.name;
      const fileExtension = fileUploaded.name.split('.').pop()?.toLowerCase();
      if (fileUploaded.size > allowedFileSize) {
        const sizeMB = (allowedFileSize / (1024 * 1024)).toFixed(0);
        setError('filesize');
        try {
          enqueueSnackbar(
            `This file size exceed the maximum limit of ${sizeMB}MB.`,
            {
              variant: 'error',
            }
          );
        } catch {}
      } else if (
        !fileExtension ||
        !validExtensions.includes(`.${fileExtension}`)
      ) {
        try {
          enqueueSnackbar(alertMessage, {variant: 'error'});
        } catch {}
      } else {
        const normalizedFileName = `${fileNameWithoutExtension}.${fileExtension}`;
        setFileName(normalizedFileName);
        // Read into memory immediately — Android content URIs can be revoked
        // after the file picker closes, causing ERR_NETWORK on delayed uploads
        const buffer = await fileUploaded.arrayBuffer();
        const inMemoryFile = new File([buffer], fileUploaded.name, {
          type: fileUploaded.type,
        });
        setFile(inMemoryFile);
        setError('');
        handleFileConfirm && handleFileConfirm();
      }
    } else {
      // setFileName(NO_FILE_NAME);
    }
  };

  const checkAndDownloadFile = async () => {
    if (file) {
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = url;
      a.download = sanitisedFileName;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    if (typeof file !== 'object') {
      if (error) {
        setLoading(false);
        return;
      }
      setError('nofile');
      setLoading(false);
      return;
    } else if (error) {
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append(formFileName, file, fileName);
    try {
      await handleSubmit(formData);
      setLoading(false);
    } catch (error: any) {
      setError(error?.message);
      setLoading(false);
      return;
    }
  };

  useEffect(() => {
    if (submitFile) {
      (async () => {
        await onSubmit();
      })();
    }
  }, [submitFile, changeFile]);

  const updateFileFromUrl = async (fileUrl: string) => {
    const fileBlob = await getFileBlobFromUrl(fileUrl);
    setFile(fileBlob);
  };

  useEffect(() => {
    if (initialFile?.length) {
      setFileName(getFileNameFromFileUrl(initialFile));
      updateFileFromUrl(initialFile);
    }
  }, [initialFile]);

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" lineHeight={'18px'}>
          {title}
        </Typography>
      )}
      {initialFile != null ? (
        initialFile.length ? (
          isSystemGenerated ? (
            <Box
              mt={1}
              border={'1px solid'}
              borderColor={'primary.main'}
              borderRadius={'12px'}
            >
              <Box
                display={'flex'}
                alignItems={'center'}
                py={{xxs: 1.875, md: 2}}
                px={{xxs: 1.875, md: 2.375}}
                gap={1.375}
              >
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  width={24}
                  height={24}
                  flexShrink={0}
                >
                  <Image
                    src={isAppIimjobs ? ResumeAttached : HiristResumeAttached}
                    width={24}
                    height={24}
                    alt="resume"
                  />
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'flex-start'}
                  alignItems={'flex-start'}
                  gap={0.5}
                >
                  <Typography color={'black'} variant="body2" lineHeight="16px">
                    {sanitisedFileName}
                  </Typography>
                  <Typography
                    component={'span'}
                    color={'primary.main'}
                    variant="subtitle3"
                    lineHeight="16px"
                    sx={{cursor: 'pointer'}}
                    onClick={checkAndDownloadFile}
                  >
                    {`Download (${formatBytes(file?.size ?? 0)})`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    ml: 'auto',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.main',
                    borderRadius: '12px',
                    px: 1,
                    py: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '9px',
                      fontWeight: 700,
                      color: 'white',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      marginTop: '2px',
                    }}
                  >
                    SYSTEM GENERATED
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'primary.main',
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                  px: 2,
                  py: 1.5,
                  backgroundColor: '#FFF7F3',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    mb: 0.5,
                  }}
                >
                  Recruiters prefer candidates who upload their own resume.
                </Typography>
                <Typography sx={{fontSize: '12px', color: '#666', mb: 1.5}}>
                  You&apos;re 3x more likely to get recruiter actions.
                </Typography>
                <Box
                  onClick={() =>
                    !loading && hiddenFileReInput?.current?.click()
                  }
                  sx={{
                    border: '1px dashed',
                    borderColor: 'primary.main',
                    borderRadius: '13px',
                    padding: '10px 14px',
                    cursor: loading ? 'default' : 'pointer',
                    display: 'flex',
                    flexDirection: {xxs: 'column', md: 'row'},
                    alignItems: 'center',
                    backgroundColor: 'white',
                    justifyContent: 'center',
                    gap: {xxs: 0.5, md: 1},
                    mt: 1.5,
                  }}
                >
                  {loading ? (
                    <>
                      <Loader size={12} />
                      <Typography
                        component="span"
                        sx={theme => ({
                          fontSize: '14px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          color: theme.palette.primary.main,
                        })}
                      >
                        Uploading Your Resume...
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        {!isAppIimjobs && (
                          <Image
                            src={UploadHirist}
                            width={16}
                            height={16}
                            alt="upload"
                          />
                        )}
                        <Typography
                          component="span"
                          sx={theme => ({
                            fontSize: '14px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            color: theme.palette.primary.main,
                          })}
                        >
                          {buttonText}
                        </Typography>
                      </Box>
                      <Typography
                        component="span"
                        sx={{
                          color: '#7E7E7E',
                          fontSize: '10px',
                          textAlign: 'center',
                        }}
                      >
                        {`(${alertMessage})`}
                      </Typography>
                      <UploadInput
                        ref={hiddenFileReInput}
                        handleUpload={handleFileUpload}
                        accept={allowedFileFormat}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              width={'100%'}
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'flex-start'}
              py={{xxs: 1.875, md: 2}}
              px={{xxs: 1.875, md: 2.375}}
              border={'1px solid #E5E5E5'}
              borderRadius={8}
              mt={1}
              gap={1.375}
              sx={{
                ':hover': {
                  borderColor: '#33333390',
                },
              }}
            >
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={24}
                height={24}
              >
                <Image
                  src={isAppIimjobs ? ResumeAttached : HiristResumeAttached}
                  width={24}
                  height={24}
                  alt="resume"
                />
              </Box>
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                gap={1.125}
                width={'100%'}
              >
                <Typography
                  color="secondary.main"
                  variant="body2"
                  lineHeight="16px"
                >
                  {loading ? (
                    <Box
                      sx={{display: 'flex', alignItems: 'center', gap: '4px'}}
                    >
                      <Loader size={12} />
                      <Typography sx={{fontSize: '12px'}}>
                        {profileStatus
                          ? 'Uploading Your Resume...'
                          : 'Parsing Your Resume...'}
                      </Typography>
                    </Box>
                  ) : (
                    `${sanitisedFileName} ${
                      uploadTimeStamp?.length &&
                      uploadTimeStamp != 'Invalid Date'
                        ? `(Uploaded On: ${uploadTimeStamp})`
                        : ''
                    }`
                  )}
                </Typography>
                <Box
                  display={'flex'}
                  justifyContent={'flex-start'}
                  alignItems={'center'}
                  gap={1}
                >
                  <ButtonV2
                    component={'label'}
                    disableRipple
                    color="primary"
                    sx={{
                      border: '0px',
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '20px',
                      padding: 0,
                      ':hover': {
                        backgroundColor: '#FFF',
                      },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {newUploadText}
                    <UploadInput
                      ref={hiddenFileReInput}
                      handleUpload={handleFileUpload}
                      accept={allowedFileFormat}
                    />
                  </ButtonV2>
                  <Typography
                    component={'label'}
                    color="primary.main"
                    variant="caption"
                    lineHeight="10px"
                  >
                    |
                  </Typography>
                  <Typography
                    component={'label'}
                    color="primary.main"
                    variant="subtitle3"
                    lineHeight="16px"
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={checkAndDownloadFile}
                    whiteSpace="nowrap"
                  >
                    {`${downloadText} (${formatBytes(file?.size ?? 0)})`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        ) : (
          <Box>
            <Box
              display="flex"
              mt={1}
              mb={1}
              alignItems={'center'}
              justifyContent={'flex-start'}
              gap={2.25}
            >
              <ButtonV2
                disableRipple
                variant="outlined"
                color="primary"
                component="label"
                sx={{
                  justifyContent: 'flex-start',
                  width: {xxs: 180, md: 260},
                  height: 40,
                  textAlign: 'left',
                  cursor: 'pointer',
                  lineHeight: '16px',
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  padding: 1.5,
                  ':hover': {
                    textDecoration: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {buttonText}
                <UploadInput
                  ref={hiddenFileInput}
                  handleUpload={handleFileUpload}
                  accept={allowedFileFormat}
                />
              </ButtonV2>
              <Link
                variant="subtitle2"
                color={
                  !file && (error === 'nofile' || nofileError)
                    ? '#D11124 !important'
                    : '#7E7E7E !important'
                }
                onClick={checkAndDownloadFile}
                sx={{
                  padding: 0,
                  lineHeight: '16px',
                  ':hover': {
                    textDecoration: 'underline',
                    cursor: file ? 'pointer' : 'not-allowed',
                    color:
                      !file && (error === 'nofile' || nofileError)
                        ? '#D11124 !important'
                        : '#7E7E7E !important',
                  },
                }}
              >
                {loading ? (
                  <Loader
                    size={16}
                    sx={{
                      color: '#7E7E7E',
                    }}
                  />
                ) : (
                  `(${
                    file && fileName
                      ? defaultFileName?.length
                        ? defaultFileName
                        : fileName?.length > 15
                          ? truncateString(fileName, 15)
                          : fileName
                      : NO_FILE_NAME
                  })`
                )}
              </Link>
            </Box>
            <Typography
              variant="body3"
              color={error === 'filesize' ? 'error.main' : 'text.secondary'}
              lineHeight={'18px'}
            >
              {alertMessage}
            </Typography>
            {error && !['nofile', 'filesize'].includes(error) && (
              <Typography
                variant="body3"
                color="error"
                sx={{
                  marginLeft: 0.5,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        )
      ) : (
        <Box
          width={'100%'}
          display={'flex'}
          justifyContent={'flex-start'}
          alignItems={'flex-start'}
          py={2}
          px={2.375}
          border={'1px solid #E5E5E5'}
          borderRadius={8}
          mt={1}
          gap={1.375}
          height={70}
        >
          <Skeleton variant="rounded" width={24} height={24}></Skeleton>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            gap={1.5}
            width={'100%'}
          >
            <Skeleton variant="rounded" width={'50%'} height={8}></Skeleton>
            <Skeleton variant="rounded" width={'100%'} height={10}></Skeleton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UploadFile;
